import type { ProblemDetails } from '../models/problem-details';
import { CSRF_HEADER, readCsrfToken } from '../session/csrf-token';
import { clearSession } from '../session/session-store';
import { ApiError } from './api-error';

/** Relative on purpose: the dev server proxies it, and a build never carries an API host. */
const API_BASE = '/api/v1';

const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];

interface RequestOptions {
    /**
     * Whether a 401 on this call means the customer's session has ended. Anonymous routes — the
     * booking calendar, the blog — answer 401 for nothing, so only calls that expect a session ask
     * for the sign-out behaviour.
     */
    authenticated?: boolean;
    body?: unknown;
    signal?: AbortSignal;
}

export async function apiGet<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return send<T>('GET', path, options);
}

/**
 * A file rather than JSON, with the same session handling as every other call.
 *
 * It exists because a case document cannot be fetched with a plain `<a href>`: the download has to
 * go through the same error handling as everything else, and an anchor gives the page no way to
 * read a problem document. The session cookie itself would ride along on an anchor perfectly well —
 * which it could not under the bearer token this replaced.
 */
export async function apiGetBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const response = await fetchOrThrow('GET', path, options);

    if (!response.ok) {
        handleUnauthorized(response, options);

        // The body of a failed download is a problem document, not the file — read it as one so a
        // withdrawn document says so rather than downloading an error page.
        throw new ApiError(response.status, (await readJson(response)) as ProblemDetails | undefined);
    }

    return response.blob();
}

/**
 * A file composed from a request body: a document the server writes from wording the page sends.
 *
 * The file name comes back with it, because the server names the file and the client should not
 * carry a second copy of that rule. Null when a proxy stripped the header.
 */
export async function apiPostBlob(
    path: string,
    options: RequestOptions = {}
): Promise<{ blob: Blob; fileName: string | null }> {
    const response = await fetchOrThrow('POST', path, options);

    if (!response.ok) {
        handleUnauthorized(response, options);
        throw new ApiError(response.status, (await readJson(response)) as ProblemDetails | undefined);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const fileName = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? null;

    return { blob: await response.blob(), fileName };
}

export async function apiPost<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return send<T>('POST', path, options);
}

export async function apiPut<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return send<T>('PUT', path, options);
}

/**
 * One request, one answer.
 *
 * There is no retry and no refresh here any more. Under the token pair this replaced, an
 * authenticated call had to check the access token's expiry, exchange the refresh token if it was
 * due, send the request, and on a 401 exchange again and resend — with a single-flight guard around
 * the exchange, because the refresh token was single-use and two in flight signed the customer out.
 * A session cookie has no expiry the client has to manage, so a 401 means the session has ended and
 * resending would only produce a second one.
 */
async function send<T>(method: string, path: string, options: RequestOptions): Promise<T> {
    const response = await fetchOrThrow(method, path, options);

    handleUnauthorized(response, options);

    return readOrThrow<T>(response);
}

async function fetchOrThrow(
    method: string,
    path: string,
    options: RequestOptions
): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };

    if (options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
    }

    if (!SAFE_METHODS.includes(method)) {
        const csrf = readCsrfToken();

        if (csrf !== null) {
            headers[CSRF_HEADER] = csrf;
        }
    }

    try {
        return await fetch(`${API_BASE}${path}`, {
            method,
            headers,

            // Same-origin in every deployment, so the cookie would travel under the default too.
            // Stated explicitly so a future split of the origins fails visibly at CORS rather than
            // silently sending unauthenticated requests.
            credentials: 'include',
            body: bodyOf(options.body),
            signal: options.signal
        });
    } catch (cause) {
        // fetch rejects only when the request never reached the server. Status 0 marks that, so a
        // page can tell "you are offline" from "the server said no".
        if (cause instanceof DOMException && cause.name === 'AbortError') {
            throw cause;
        }

        throw new ApiError(0);
    }
}

function bodyOf(body: unknown): BodyInit | undefined {
    return body === undefined ? undefined : JSON.stringify(body);
}

/**
 * Drops the local copy of the session when the server says it is over, so the portal shows the
 * signed-out shell instead of a page whose every panel then fails.
 *
 * The server has already cleared the cookie by this point; this is the browser catching up.
 */
function handleUnauthorized(response: Response, options: RequestOptions): void {
    if (response.status === 401 && options.authenticated === true) {
        clearSession();
    }
}

async function readOrThrow<T>(response: Response): Promise<T> {
    if (response.status === 204) {
        return undefined as T;
    }

    const payload = await readJson(response);

    if (!response.ok) {
        // A failure that could not be parsed still has its status, which is the part callers map to
        // a message. The sentinel is not a problem document and must never be handed on as one.
        throw new ApiError(
            response.status,
            payload === UNPARSABLE ? undefined : (payload as ProblemDetails | undefined)
        );
    }

    // A success whose body could not be read is not a success, and this is the line that says so.
    //
    // It used to resolve as `undefined`, which every caller then treated as data: `useSpecialists`
    // stored it and the landing page threw calling `.some` on it, taking down a page that has its
    // own content and needs the API for one section. A 200 carrying HTML is not hypothetical — it is
    // what an edge proxy returns from a maintenance page, and what any static host answers an
    // unproxied `/api` path with, since the SPA fallback replies 200 with the shell.
    //
    // The empty body stays legitimate: a route that answers 200 with nothing has said something.
    if (payload === UNPARSABLE) {
        throw new ApiError(response.status, undefined);
    }

    return payload as T;
}

/** Distinguishes "the body was empty" from "the body was not JSON" — only the first is a value. */
const UNPARSABLE = Symbol('unparsable');

async function readJson(response: Response): Promise<unknown> {
    const text = await response.text();

    if (text.length === 0) {
        return undefined;
    }

    try {
        return JSON.parse(text);
    } catch {
        // A proxy error page or a truncated body. The status is kept; the payload is not a payload.
        return UNPARSABLE;
    }
}
